import { message } from "antd";

import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";

import BdsSectionBox from "commonComponents/bdsWrappers/BdsSectionBox/BdsSectionBox";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

import { getFactorDetail, getFactorTypeLabel, getManageableFactors } from "./mfaFactor.utils";
import { useMyAccountMfa } from "./useMyAccountMfa";

const LAST_FACTOR_MESSAGE =
    "You must keep at least one authenticator method. Add another before removing this one.";

export const Auth0MyAccountMfa: React.FC = () => {
    const { factors, isElevated, isLoading, prime, removeFactor } = useMyAccountMfa();

    // Hide auto-attached, non-MFA methods (password/email/recovery-code). They are neither shown nor
    // counted toward the last-factor guard, so they can't block removing a real authenticator.
    const manageableFactors = factors === undefined ? undefined : getManageableFactors(factors);
    const isLastFactor = manageableFactors?.length === 1;

    // Best-effort, client-side last-factor guard. BDS guidance is to keep the button enabled and
    // explain on click rather than disable it with a tooltip that never shows (BTButton disabled +
    // popover is a no-op). Not a security control — bypassable outside the BTNet UI until an Auth0
    // Action/policy enforces it server-side.
    const handleRemove = (factorId: string, label: string) => {
        if (isLastFactor) {
            void message.info(LAST_FACTOR_MESSAGE);
            return;
        }
        btDeleteConfirm({
            entityName: label,
            onOk: async () => {
                await removeFactor(factorId);
                void message.success("Authenticator method removed.");
            },
        });
    };

    return (
        <BdsSectionBox title="Two-factor authentication methods">
            {!isElevated ? (
                <div className="flex flex-col gap-2">
                    <BTText size="distinct-sm">
                        {factors === undefined
                            ? "Verify your identity to view and manage your authenticator methods."
                            : "Session expired — re-verify to continue managing your methods."}
                    </BTText>
                    <div>
                        <BTButton
                            type="primary"
                            data-testid="mfaManage-verify"
                            onClick={() => void prime()}
                            disabled={isLoading}
                        >
                            {factors === undefined ? "Manage MFA" : "Session expired — re-verify"}
                        </BTButton>
                    </div>
                </div>
            ) : factors === undefined ? (
                // Full-section spinner only during the initial load (before any factors arrive).
                // Mutations (remove/verify) also flip isLoading, but must NOT swap the whole list to
                // a spinner — that would unmount the enroll panel mid-verify and fire a second
                // challenge. Per-control disabled={isLoading} handles busy state during mutations.
                <BTLoading />
            ) : (
                <div className="flex w-full flex-col gap-3" data-testid="mfaFactor-list">
                    {manageableFactors?.map((factor) => {
                        const detail = getFactorDetail(factor);
                        // Guard against a missing/malformed created_at: new Date("") is an Invalid
                        // Date, which passes DateDisplayDate's null-check and then throws inside
                        // date-fns format. Only render the "Added" line when the date is valid.
                        const createdDate = new Date(factor.created_at);
                        const hasCreatedDate = !Number.isNaN(createdDate.getTime());
                        return (
                            <div
                                key={factor.id}
                                className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2"
                                data-testid={`mfaFactor-row-${factor.id}`}
                            >
                                {/* min-w-0 lets a long detail (e.g. an email) wrap/fold instead of
                                    forcing the row wider than the section. */}
                                <div className="flex min-w-0 flex-col">
                                    <BTText size="distinct-sm">
                                        {getFactorTypeLabel(factor.type)}
                                    </BTText>
                                    {detail !== undefined && (
                                        <BTText size="normal-sm">{detail}</BTText>
                                    )}
                                    {hasCreatedDate && (
                                        <div className="flex items-center gap-1">
                                            <BTText size="normal-sm">Added</BTText>
                                            {/* DateDisplayDate renders an unstyled span, so it
                                                inherits the ambient 14px font. Wrap it in BTText
                                                so the date picks up the same normal-sm token as
                                                the label beside it. */}
                                            <BTText size="normal-sm">
                                                <DateDisplayDate
                                                    data-testid={`mfaFactor-created-${factor.id}`}
                                                    value={createdDate}
                                                />
                                            </BTText>
                                        </div>
                                    )}
                                </div>
                                {/* shrink-0 keeps the badge + button at their natural width so the
                                    button text never overflows; the group wraps to the next line as a
                                    unit on narrow screens. */}
                                <div className="flex shrink-0 items-center gap-2">
                                    <BTStatusBadge
                                        testid={`mfaFactor-badge-${factor.id}`}
                                        text="Enrolled"
                                        displayType="success"
                                    />
                                    <BTButton
                                        type="secondary"
                                        data-testid={`mfaFactor-remove-${factor.id}`}
                                        onClick={() =>
                                            handleRemove(factor.id, getFactorTypeLabel(factor.type))
                                        }
                                        disabled={isLoading}
                                    >
                                        Remove
                                    </BTButton>
                                </div>
                            </div>
                        );
                    })}
                    {manageableFactors?.length === 0 && (
                        <BTText size="distinct-sm">No authenticator methods enrolled.</BTText>
                    )}
                </div>
            )}
        </BdsSectionBox>
    );
};
